/* eslint-disable react/react-in-jsx-scope */
import { z } from "zod";

import TypeEntreprise from "@/components/layout/TypeEntreprise";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import { toast } from "@/components/ui/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Building,
  Eye,
  EyeOff,
  Layers,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import PasswordStrengthCheck from "./PasswordStrengthCheck";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

const RegistrationForm = () => {
  const navigate = useNavigate();
  const { signIn, register, loading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const { t } = useTranslation("auth", {
    keyPrefix: "register",
  });

  const [loadingSearch, setLoadingSearch] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [nameOrg, setNameOrg] = useState("");
  const [selectedOrganisation, setSelectedOrganisation] = useState<any>(null);
  const [errorOrg, setErrorOrg] = useState("");
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  const [step, setStep] = useState(1);
  const [activeTab, setActiveTab] = useState("personal");
  const [isEmailAvailable, setIsEmailAvailable] = useState(false);
  const [loadingCheckMail, setLoadingCheckMail] = useState(false);

  const [mailError, setMailError] = useState("");

  // Nouveaux états
  const [isParticulier, setIsParticulier] = useState(false);
  const [manualStructure, setManualStructure] = useState(false);

  useEffect(() => {
    if (activeTab === "personal") {
      setStep(1);
    } else if (activeTab === "organisation") {
      setStep(2);
    } else {
      setStep(3);
    }
  }, [activeTab]);

  const fetchResults = async (type = 1) => {
    setLoadingSearch(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_ENDPOINT}/financer/search/external?s=${query}&type=1}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      setResults(data.list || []);
    } catch (error) {
      console.error("Erreur lors de la récupération des résultats :", error);
      setResults([]);
    } finally {
      setLoadingSearch(false);
    }
  };

  useEffect(() => {
    if (query === undefined || query.length < 3) {
      setResults([]);
      return;
    }
    if (query.length > 0) {
      setSelectedOrganisation(null);
    }
    const delayDebounceFn = setTimeout(() => {
      fetchResults(1);
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  useEffect(() => {
    if (localStorage.getItem("code")) {
      const code = localStorage.getItem("code");
      if (code !== import.meta.env.VITE_API_CODE) {
        navigate("/access-code");
      }
    } else {
      navigate("/access-code");
    }
  }, []);

  // Schéma de validation dynamique
 const formSchema = z
  .object({
    lastname: z.string().min(2),
    firstname: z.string().min(2),
    structureName: z.string().optional(),
    email: z.string().regex(emailRegex),
    password: z.string().min(8),
    confirmPassword: z.string().min(8).regex(passwordRegex),
    siret: z.string().optional(),
    numeroVoie: z.string().optional(),
    rue: z.string().optional(),
    codePostal: z.string().optional(),
    ville: z.string().optional(),
    pays: z.string().optional(),
    cacs: z.array(z.string()).optional(),
    cac1Sub: z.string().optional(),
    isParticulier: z.boolean(),
  })
  .superRefine((data, ctx) => {
    // Vérification mot de passe
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        path: ["confirmPassword"],
        message: "Les mots de passe ne correspondent pas.",
        code: z.ZodIssueCode.custom,
      });
    }

    // Si ce n'est pas un particulier, les champs organisation sont obligatoires
    if (!data.isParticulier) {
      if (!data.structureName || data.structureName.trim().length < 2) {
        ctx.addIssue({
          path: ["structureName"],
          message: "Le nom de la structure est obligatoire.",
          code: z.ZodIssueCode.custom,
        });
      }
      if (!data.siret || data.siret.trim().length < 1) {
        ctx.addIssue({
          path: ["siret"],
          message: "Le numéro d’identification est obligatoire.",
          code: z.ZodIssueCode.custom,
        });
      }
      // Tu peux faire pareil pour les autres champs si tu veux les rendre obligatoires
      // Exemple: numeroVoie, rue, codePostal, ville, pays
    }
  });


  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstname: "",
      lastname: "",
      structureName: "",
      email: "",
      password: "",
      confirmPassword: "",
      siret: "",
      numeroVoie: "",
      rue: "",
      codePostal: "",
      ville: "",
      pays: "",
      cacs: [],
      cac1Sub: "",
      isParticulier: false,
    },
  });

  const {
    register: formRegister,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = form;

  const { setError } = form;
  const password = form.watch("password");
  const [isStep2Disabled, setIsStep2Disabled] = useState(true);
  const [isStep3Disabled, setIsStep3Disabled] = useState(true);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const {
      success,
      errors,
      existingUserUpdated,
      passwordInitialized,
      message,
    } = await register(
      values.firstname,
      values.lastname,
      values.structureName,
      values.email,
      values.password,
      values.confirmPassword,
      values.siret,
      "",
      values.numeroVoie,
      values.rue,
      values.codePostal,
      values.ville,
      values.pays,
      values.cacs,
      values.cac1Sub,
      values.isParticulier
    );
    if (success) {
      if (existingUserUpdated) {
        if (passwordInitialized) {
          await signIn(values.email, values.password);
          navigate(`/register/verify`);
          return;
        }

        toast({
          title: t("Role ajoute"),
          description:
            message ??
            t(
              "Le role porteur de projet a ete ajoute a votre compte existant. Connectez-vous avec votre mot de passe actuel."
            ),
        });
        navigate(
          `/login?reusedAccount=1&email=${encodeURIComponent(values.email)}`
        );
        return;
      }

      await signIn(values.email, values.password);
      navigate(`/register/verify`);
    } else {
      if (errors) {
        errors.forEach((error: any) => {
          setError(error[0], {
            type: "server",
            message: error[1],
          });
        });
      } else {
        setError("password", {
          type: "server",
          message: t("Une erreur est survenue, veuillez reessayer."),
        });
      }
    }
  }

  useEffect(() => {
    const isValid =
      password?.length >= 8 &&
      /[a-z]/.test(password) &&
      /[A-Z]/.test(password) &&
      /\d/.test(password) &&
      /[!@#$%^&*(),.?":{}|<>]/.test(password);
    setIsPasswordValid(isValid);
  }, [password]);

  return (
    <div className="mx-auto mb-5 mt-8 flex flex-wrap justify-center lg:w-3/4">
      <div className="w-full md:w-3/4 md:pl-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("Créez votre compte")}</CardTitle>
            <CardDescription>
              {t("Pour soumettre votre projet et bénéficier du soutien de Verdeo, veuillez créer un compte. Cela vous permettra de suivre vos projets, recevoir des mises à jour et accéder aux outils de traçabilité gratuits.")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <Tabs
                  defaultValue="personal"
                  className="w-full mt-4"
                  value={activeTab}
                  onValueChange={setActiveTab}
                >
                  <TabsList className="relative flex w-full rounded-2xl bg-white p-1 shadow-md">
                    <TabsTrigger
                      value="personal"
                      className="relative flex-1 rounded-xl px-5 py-3 text-sm font-medium transition-all 
                                text-gray-600 hover:text-[#1d5214] 
                                data-[state=active]:bg-[#1d5214] data-[state=active]:text-white 
                                data-[state=active]:shadow-md data-[state=active]:scale-[1.02]"
                    >
                      {t("Informations personnelles")}
                    </TabsTrigger>

                    <TabsTrigger
                      value="organisation"
                      disabled={!isParticulier && isStep2Disabled}
                      className="relative flex-1 rounded-xl px-5 py-3 text-sm font-medium transition-all 
                                text-gray-600 hover:text-[#1d5214] 
                                data-[state=active]:bg-[#1d5214] data-[state=active]:text-white 
                                data-[state=active]:shadow-md data-[state=active]:scale-[1.02]"
                    >
                      {t("Informations sur l'organisation")}
                    </TabsTrigger>

                    <TabsTrigger
                      value="activities"
                      disabled={isStep3Disabled || isParticulier}
                      className="relative flex-1 rounded-xl px-5 py-3 text-sm font-medium transition-all 
                                text-gray-600 hover:text-[#1d5214] 
                                data-[state=active]:bg-[#1d5214] data-[state=active]:text-white 
                                data-[state=active]:shadow-md data-[state=active]:scale-[1.02]"
                    >
                      {t("Description des activités")}
                    </TabsTrigger>

                  </TabsList>

                  {/* ... Étape 1 = inchangé ... */}
                  {/* Informations personnelles */}
                  <TabsContent value="personal" asChild className="mt-4">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-4 p-4 bg-white rounded-xl shadow"
                    >
                      <h4 className="flex items-center text-lg font-semibold text-gray-800 mb-3">
                        <User className="mr-2 text-[#1d5214] w-6 h-6" />
                        {t("Informations personnelles")}
                      </h4>
                      <hr className="mt-4 border-gray-300" />

                      <FormField
                        control={form.control}
                        name="lastname"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("Nom")}</FormLabel>
                            <FormControl>
                              <Input placeholder={t("Nom")} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="firstname"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("Prénom")}</FormLabel>
                            <FormControl>
                              <Input placeholder={t("Prénom")} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("Mail")}</FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t("Mail")}
                                {...field}
                                type="email"
                                onChange={(e) => {
                                  field.onChange(e);
                                  setIsEmailAvailable(false);
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                            <p className="text-red-500">{mailError}</p>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("Mot de passe")}</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  placeholder={t("Mot de passe")}
                                  {...field}
                                  type={showPassword ? "text" : "password"}
                                />
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setShowPassword(!showPassword);
                                  }}
                                  className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center"
                                >
                                  {!showPassword ? <EyeOff /> : <Eye />}
                                </button>
                              </div>
                            </FormControl>
                            <FormMessage />
                            <PasswordStrengthCheck
                              password={password}
                              isPasswordValid={isPasswordValid}
                              className="mt-0"
                            />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="confirmPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("Confirmation mot de passe")}</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  placeholder={t("Confirmation mot de passe")}
                                  {...field}
                                  type={showPasswordConfirm ? "text" : "password"}
                                />
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setShowPasswordConfirm(!showPasswordConfirm);
                                  }}
                                  className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center"
                                >
                                  {!showPasswordConfirm ? <EyeOff /> : <Eye />}
                                </button>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </motion.div>
                  </TabsContent>


                  {/* Organisation */}
                  {/* Organisation */}
                  {/* Organisation */}
                  <TabsContent value="organisation">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-6 p-6 bg-white rounded-xl shadow-lg"
                    >
                      <h4 className="flex items-center text-lg font-semibold text-gray-800 mb-4">
                        <Building className="mr-2 text-[#1d5214] w-6 h-6" />
                        {t("Informations sur l'organisation")}
                      </h4>
                      <hr className="border-gray-300 mb-4" />

                      {/* Switch particulier */}
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-700">Je suis un particulier</span>
                        <button
                          type="button"
                          className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors duration-300 ${
                            isParticulier ? "bg-green-500" : "bg-gray-300"
                          }`}
                          onClick={() => {
                            const newValue = !isParticulier;
                            setIsParticulier(!isParticulier);
                            form.setValue("isParticulier", newValue);
                          }}
                        >
                          <span
                            className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ${
                              isParticulier ? "translate-x-6" : "translate-x-0"
                            }`}
                          />
                        </button>
                      </div>

                      {!isParticulier && (
                        <>
                          {/* Nom de la structure + toggle saisie manuelle */}
                          <FormField
  control={form.control}
  name="structureName"
  rules={{
    required:
      !isParticulier ? "Le nom de la structure est obligatoire" : false,
  }}
  render={({ field, fieldState }) => (
    <FormItem className="w-full">
      {/* Label + toggle */}
      <div className="flex items-center justify-between mb-2">
        <FormLabel>Nom de la structure</FormLabel>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>Saisie manuelle ?</span>
          <button
            type="button"
            className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors duration-300 ${
              manualStructure ? "bg-green-500" : "bg-gray-300"
            }`}
            onClick={() => {
              setManualStructure(!manualStructure);

              // Supprime l'erreur et vide les champs
              form.clearErrors("structureName");
              if (!manualStructure) {
                setNameOrg("");
                setQuery("");
                setResults([]);
                field.onChange("");
              } else {
                field.onChange("");
              }
            }}
          >
            <span
              className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ${
                manualStructure ? "translate-x-6" : "translate-x-0"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Full width input */}
      <div className="w-full">
        {/* Recherche entreprise */}
        {!manualStructure && (
          <div className="relative w-full">
            <p className="text-sm text-gray-500 mb-1">
              Saisissez au moins 3 caractères pour rechercher l’organisation
            </p>
            <Input
              type="text"
              placeholder="Rechercher une entreprise"
              value={nameOrg}
              className="w-full"
              onChange={(e) => {
                setNameOrg(e.target.value);
                setQuery(e.target.value);
                if (fieldState.error) form.clearErrors("structureName");
              }}
            />
            {loadingSearch && (
              <p className="absolute mt-2 text-sm rounded-md border border-gray-300 bg-white shadow-lg px-4 py-2 text-gray-500">
                Chargement...
              </p>
            )}
            {!manualStructure && !loadingSearch &&
              query && results.length > 0 && (
              <ul className="absolute mt-2 max-h-60 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg z-50 w-full">
                {results.map((result, i) => (
                  <li
                    key={i}
                    className="flex cursor-pointer gap-2 border-b px-4 py-2 last:border-b-0 hover:bg-blue-100"
                    onClick={() => {
                      setSelectedOrganisation(result);
                      setNameOrg(result.nom_entreprise);
                      form.setValue(
                        "structureName",
                        result.nom_entreprise,
                        { shouldValidate: true }
                      );
                      form.setValue(
                        "siret",
                        result.siege.siret,
                        { shouldValidate: true }
                      );
                     
                      form.setValue(
                        "numeroVoie",
                        result.siege?.numero_voie
                          ? String(
                              result.siege.numero_voie
                            )
                          : "",
                        { shouldValidate: true }
                      );
                      form.setValue(
                        "rue",
                        result.siege.type_voie +
                          " " +
                          result.siege.libelle_voie,
                        { shouldValidate: true }
                      );
                      form.setValue(
                        "codePostal",
                        result.siege.code_postal,
                        { shouldValidate: false }
                      );
                      form.setValue(
                        "ville",
                        result.siege.ville,
                        { shouldValidate: true }
                      );
                      form.setValue(
                        "pays",
                        result.siege.pays,
                        { shouldValidate: true }
                      );
                      setQuery("");
                      setResults([]);
                    }}
                  >
                    {result.nom_entreprise}
                  </li>
                ))}
              </ul>
            )}
            {fieldState.error && (
              <p className="text-red-500 text-sm mt-1">{fieldState.error.message}</p>
            )}
          </div>
        )}

        {/* Saisie manuelle */}
        {manualStructure && (
          <div className="w-full">
            <p className="text-sm text-gray-500 mb-1">
              Veuillez saisir le nom de l’organisation
            </p>
            <Input
              type="text"
              placeholder="Saisie libre"
              {...field}
              className="w-full"
              onChange={(e) => {
                field.onChange(e);
                if (fieldState.error) form.clearErrors("structureName");
              }}
            />
            {fieldState.error && (
              <p className="text-red-500 text-sm mt-1">{fieldState.error.message}</p>
            )}
          </div>
        )}
      </div>
    </FormItem>
  )}
/>


                          {/* Numéro d'identification (OU SIRET) */}
                          <FormField
                            control={form.control}
                            name="siret"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Numéro d'identification (OU SIRET)</FormLabel>
                                <FormControl>
                                  <Input placeholder={"Numéro d'identification"} {...field} required />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Adresse */}
                          <div className="grid grid-cols-2 gap-4">
                            <FormField
                              control={form.control}
                              name="numeroVoie"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t("Numéro de voie")}</FormLabel>
                                  <FormControl>
                                    <Input placeholder={t("Numéro de voie")} {...field} required />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="rue"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t("Rue")}</FormLabel>
                                  <FormControl>
                                    <Input placeholder={t("Rue")} {...field} required />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <div className="grid grid-cols-3 gap-4">
                            <FormField
                              control={form.control}
                              name="codePostal"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t("Code postal")}</FormLabel>
                                  <FormControl>
                                    <Input placeholder={t("Code postal")} {...field} required />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="ville"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t("Ville")}</FormLabel>
                                  <FormControl>
                                    <Input placeholder={t("Ville")} {...field} required />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="pays"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t("Pays")}</FormLabel>
                                  <FormControl>
                                    <Input placeholder={t("Pays")} {...field} required />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        </>
                      )}
                    </motion.div>
                  </TabsContent>

                  {/* Activités */}
                  {!isParticulier && (
                    <TabsContent value="activities">
                      <motion.div className="space-y-4 p-4 bg-white rounded-xl shadow">
                        <h4 className="flex items-center text-lg font-semibold text-gray-800 mb-3">
                          <Layers className="mr-2 text-[#1d5214] w-6 h-6" />
                          {t("Description des activités")}
                        </h4>
                        <TypeEntreprise register={formRegister} watch={watch} setValue={setValue} />
                      </motion.div>
                    </TabsContent>
                  )}
                </Tabs>

                <div className="grid grid-cols-2 gap-2">
                  {step > 1 && !isParticulier && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const newStep = step - 1;
                        setStep(newStep);

                        // Changer l'onglet actif selon le step
                        const stepToTabMap = {
                          1: "personal",
                          2: "organisation",
                          3: "activities",
                        };
                        setActiveTab(stepToTabMap[newStep]);
                      }}
                    >
                      {t("Précédent")}
                    </Button>
                  )}


                  {/* ⚡ Bouton Step 1 ou Step 2 */}
                  {step < 3 && !loadingCheckMail && (
                    <>
                      {step === 1 && (
                        <Button
                          type="button"
                          onClick={async () => {
                            // Vérification des champs personnels et email
                            const valid = await trigger([
                              "lastname",
                              "email",
                              "firstname",
                              "password",
                              "confirmPassword",
                            ]);
                            if (valid) {
                              setLoadingCheckMail(true);
                              try {
                                const response = await fetch(
                                  `${import.meta.env.VITE_API_ENDPOINT}/check-existing-user`,
                                  {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({
                                      email: form.getValues("email"),
                                      targetRole: "ROLE_CARRIER",
                                    }),
                                  }
                                );
                                const json = await response.json();
                                if (json.success) {
                                  setIsEmailAvailable(true);
                                  setActiveTab("organisation");
                                  setStep(2);
                                  setIsStep2Disabled(false);
                                  setMailError("");
                                } else {
                                  setIsEmailAvailable(false);
                                  setMailError(t("Cet adresse email est deja utilisé"));
                                }
                              } catch {
                                setMailError(t("Cet adresse email est deja utilisé"));
                              } finally {
                                setLoadingCheckMail(false);
                              }
                            }
                          }}
                        >
                          {t("Suivant")}
                        </Button>
                      )}

                      {step === 2 && !isParticulier && (
                        <Button
                          type="button"
                          onClick={async () => {
                            const valid = await trigger(["structureName", "siret"]);
                            if (valid) {
                              setActiveTab("activities");
                              setStep(3);
                              setIsStep3Disabled(false);
                            }
                          }}
                        >
                          {t("Suivant")}
                        </Button>
                      )}
                    </>
                  )}

                  {/* ⚡ Animation de chargement pendant la vérif email */}
                  {loadingCheckMail && (
                    <Button type="button" disabled>
                      <LoadingSpinner textChargement={t("Vérification du mail en cours..")} />
                    </Button>
                  )}

                  {/* ⚡ Submit pour particulier ou Step 3 */}
                  {(isParticulier && step === 2) || step === 3 ? (
                    <Button disabled={loading} type="submit" className="w-full">
                      {loading ? "..." : t("S'inscrire")}
                    </Button>
                  ) : null}

                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegistrationForm;
